import { useDispatch, useSelector } from 'react-redux';

import { authentication } from '@perpay-web/authentication/singleton';
import {
    analyticsPartnerHostedOnboardingInEligibleStateModalOpen,
    analyticsPartnerHostedOnboardingInEligibleStateModalClose,
    analyticsPartnerHostedOnboardingInEligibleStateModalEditAddress,
} from '@perpay-web/fintech/actions/analytics/partnerHostedOnboarding';
import { partnerOnboardingSignup } from '@perpay-web/fintech/actions/entities/partnerOnboarding';
import { updateUserInfoForCardApplication } from '@perpay-web/fintech/actions/entities/userInfo';
import CardApplicationPersonalDetails from '@perpay-web/fintech/components/composite/CardApplicationPersonalDetails/CardApplicationPersonalDetails';
import { PARTNER_ONBOARDING_CARD } from '@perpay-web/fintech/constants/partnerOnboardingCodes';
import { partnerOnboardingSignupDataModule } from '@perpay-web/fintech/dataModules/partnerOnboardingSignup';
import { updateUserInfoForCardApplicationDataModule } from '@perpay-web/fintech/dataModules/updateUserInfoForCardApplication';
import {
    cardApplicationConfirmAddressModalClose,
    cardApplicationConfirmAddressModalSetAptValidationRequiredFlag,
    useCardApplicationConfirmAddressModalContext,
} from '@perpay-web/fintech/hooks/useCardApplicationConfirmAddressModalContext';
import { emailMisspelledModalSetIsModalOpen } from '@perpay-web/fintech/hooks/useEmailMispelledModalContext';
import { partnerOnboardingSignupIneligibleStateModalSetIsModalOpen } from '@perpay-web/fintech/hooks/usePartnerOnboardingSignupIneligibleStateModalContext';
import { useSessionStorage } from '@perpay-web/hooks/useSessionStorage';
import { splitAddressStreetIntoTwoLines } from '@perpay-web/utils/stringUtils';

/**
 * @typedef {Object} CardApplicationPersonalDetailsContainerProps
 * @property {boolean} isUserIdentityVerified
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} dateOfBirth
 * @property {string} ssn
 * @property {Object} currentUserAddress
 * @property {boolean} isExistingUser
 * @property {boolean} isExistingUserWithPassword
 * @property {string} partnerCode
 * @property {string} [offerCode] - Optional
 * @property {Object} [welcomeOffer] - Optional
 * @property {string} [productName] - Optional
 * @property {boolean} [isUserInPremiumGroup] - Optional
 * @property {string} [partnerRelease] - Optional
 */

/** @type {import('react').FC<CardApplicationPersonalDetailsContainerProps>} */
const CardApplicationPersonalDetailsContainer = ({
    isUserIdentityVerified,
    firstName,
    lastName,
    dateOfBirth,
    ssn,
    currentUserAddress,
    isExistingUser,
    isExistingUserWithPassword,
    partnerCode,
    offerCode,
    welcomeOffer,
    productName,
    isUserInPremiumGroup,
    partnerRelease,
}) => {
    const dispatch = useDispatch();
    const cardApplicationErrors =
        useSelector(updateUserInfoForCardApplicationDataModule.getErrors)
            .response || {};
    const phoErrors =
        useSelector(partnerOnboardingSignupDataModule.getErrors).response || {};
    const phoneNumber = authentication.getPhoneNumber();

    const queryString = window.location.search;

    const [isApplicationInProgress, setApplicationInProgress] =
        useSessionStorage('isApplicationInProgress', false);
    const { aptNumberValidationRequired } =
        useCardApplicationConfirmAddressModalContext();

    const addressToPrefill = isApplicationInProgress ? currentUserAddress : {};

    const [line1 = '', line2 = ''] = splitAddressStreetIntoTwoLines(
        addressToPrefill ? addressToPrefill.street1 : '',
    );

    const {
        city = '',
        state = '',
        zipcode = '',
    } = addressToPrefill || {
        city: '',
        state: '',
        zipcode: '',
    };

    const initialAddressValues = {
        street1: line1,
        street2: line2,
        city,
        state,
        zipcode,
    };

    /**
     * Handles signup for new users via Partner Hosted Onboarding (PHO).
     *
     * @param {Object} values - Form values from Formik
     * @param {boolean} [validationRequired=true] - Whether apt number validation is required
     */
    const handlePhoSignup = (values, validationRequired = true) => {
        dispatch(
            partnerOnboardingSignup({
                ...values,
                partnerCode,
                queryString,
                offerCode,
                productName,
                partnerRelease,
                onboardingProduct: PARTNER_ONBOARDING_CARD,
                aptNumberValidationRequired:
                    validationRequired && aptNumberValidationRequired,
            }),
        );
    };

    /**
     * Handles user info updates for existing users during card application.
     *
     * Transforms address fields and dispatches update action with apt number validation flag.
     *
     * @param {Object} values - Form values from Formik
     * @param {boolean} [validationRequired=true] - Whether apt number validation is required
     */
    const handleUserInfoUpdate = (values, validationRequired = true) => {
        let address = values.street1.replace(',', '');
        const addressStreet2 = values.street2.replace(',', '');
        if (values.street2) {
            address = `${address}, ${addressStreet2}`;
        }
        const transformedValues = {
            ...values,
            street_addr: address,
            partnerRelease,
            aptNumberValidationRequired: validationRequired,
        };
        dispatch(updateUserInfoForCardApplication(transformedValues));
        setApplicationInProgress(true);
    };

    return (
        <CardApplicationPersonalDetails
            onSubmit={(values) => {
                if (isExistingUser) {
                    handleUserInfoUpdate(values);
                } else {
                    handlePhoSignup(values);
                }
            }}
            errors={isExistingUser ? cardApplicationErrors : phoErrors}
            onErrorStateChange={(hasErrors) => {
                if (hasErrors) {
                    setApplicationInProgress(false);
                }
            }}
            isUserIdentityVerified={isUserIdentityVerified}
            firstName={firstName}
            lastName={lastName}
            dateOfBirth={dateOfBirth}
            ssn={ssn}
            phoneNumber={phoneNumber}
            initialAddressValues={initialAddressValues}
            welcomeOffer={welcomeOffer}
            partnerCode={partnerCode}
            isUserInPremiumGroup={isUserInPremiumGroup}
            analyticsOnOpenIneligibleStateModal={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingInEligibleStateModalOpen(),
                )
            }
            analyticsOnCloseIneligibleStateModal={() => {
                dispatch(
                    analyticsPartnerHostedOnboardingInEligibleStateModalClose(),
                );
            }}
            analyticsOnEditAddressIneligibleStateModal={() => {
                dispatch(
                    analyticsPartnerHostedOnboardingInEligibleStateModalEditAddress(),
                );
            }}
            onIneligibleStateAddress={() => {
                dispatch(
                    partnerOnboardingSignupIneligibleStateModalSetIsModalOpen(
                        true,
                    ),
                );
            }}
            onEmailMisspelled={() => {
                dispatch(emailMisspelledModalSetIsModalOpen(true));
            }}
            onConfirmAddressModalClose={() => {
                dispatch(
                    cardApplicationConfirmAddressModalSetAptValidationRequiredFlag(
                        true,
                    ),
                );
                dispatch(cardApplicationConfirmAddressModalClose());
            }}
            onConfirmAddressModalContinue={(values) => {
                // Apply phone number transformation if needed
                let transformedValues = values;
                if (!phoneNumber && values.phoneNumber) {
                    const {
                        phoneNumber: extractedPhoneNumber,
                        ...otherValues
                    } = values;
                    transformedValues = {
                        ...otherValues,
                        mobilePhone: `+1${extractedPhoneNumber}`,
                    };
                }

                if (isExistingUser) {
                    handleUserInfoUpdate(transformedValues, false);
                } else {
                    handlePhoSignup(transformedValues, false);
                }
            }}
            isExistingUser={isExistingUser}
            isExistingUserWithPassword={isExistingUserWithPassword}
        />
    );
};
export default CardApplicationPersonalDetailsContainer;
